import React from 'react'

import { Absolute, Relative } from '../helpers/Position'
import { useCanvas } from './Canvas'
import Center from '../helpers/Center'
import Circle from '../shapes/Circle'
import Icon from '../shapes/Icon'
import Panel from '../structure/Panel'
import Separator from '../helpers/Separator'
import Text from '../presentation/Text'

const calculateSize = (initSize, initClientX, initClientY, canvasWidth, e) => {
  const x = Math.round(((e.clientX - initClientX) / canvasWidth) * 100)
  const y = Math.round((e.clientY - initClientY) / 25) * 25
  const width = initSize.width + x
  const height = initSize.height + y
  return { width, height }
}

const useResize = (initialSize, onChange) => {
  const canvas = useCanvas()
  const [isResizing, setIsResizing] = React.useState(false)
  const [size, setSize] = React.useState({ width: initialSize?.width || 25, height: initialSize?.height || 400 })

  const resize = e => {
    setIsResizing(true)
    const { clientX, clientY } = e

    const onMove = e => {
      window.getSelection().removeAllRanges()
      setSize(calculateSize(size, clientX, clientY, canvas.width, e))
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener(
      'mouseup',
      e => {
        const newSize = calculateSize(size, clientX, clientY, canvas.width, e)
        setIsResizing(false)
        if (onChange) onChange({ size: newSize })
        window.removeEventListener('mousemove', onMove)
      },
      { once: true }
    )
  }

  return { size, resize, isResizing }
}

const calculatePosition = (initPosition, initClientX, initClientY, canvasWidth, e) => {
  const x = Math.round(((e.clientX - initClientX) / canvasWidth) * 100)
  const y = Math.round((e.clientY - initClientY) / 25) * 25
  let top = initPosition.top + y
  if (top <= 0) top = 0
  let left = initPosition.left + x
  if (left <= 0) left = 0
  return { top, left }
}

const useMove = (initialPosition, onChange) => {
  const canvas = useCanvas()
  const [isMoving, setIsMoving] = React.useState(false)
  const [position, setPosition] = React.useState({
    top: initialPosition?.top || 0,
    left: initialPosition?.left || 0
  })

  const move = e => {
    setIsMoving(true)
    const { clientX, clientY } = e

    const onMove = e => {
      window.getSelection().removeAllRanges()
      setPosition(calculatePosition(position, clientX, clientY, canvas.width, e))
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener(
      'mouseup',
      e => {
        const newPosition = calculatePosition(position, clientX, clientY, canvas.width, e)
        setIsMoving(false)
        if (onChange) onChange({ position: newPosition })
        window.removeEventListener('mousemove', onMove)
      },
      { once: true }
    )
  }

  return { position, move, isMoving }
}

const CanvasCard = ({ initialSize, initialPosition, children, onChange, preview, ...props }) => {
  const { disabled } = useCanvas() || { disabled: true }
  const { size, resize, isResizing } = useResize(initialSize, onChange)
  const { position, move, isMoving } = useMove(initialPosition, onChange)

  if (preview) {
    return (
      <Panel
        height={initialSize.height + 'px'}
        width={initialSize.width}
        shadow
        shadowTransparency={0.05}
        scroll
        {...props}
      >
        {children}
      </Panel>
    )
  }

  return (
    <Absolute
      zIndex={isResizing || isMoving ? 99 : 0}
      top={position.top + 'px'}
      left={position.left + '%'}
      width={size.width + '%'}
      height={size.height + 'px'}
    >
      <Separator width="100%" height="100%" padding={0.4}>
        <Relative height="100%" width="100%" zIndex={0}>
          <Panel height="100%" width="100%" shadow shadowTransparency={0.05} scroll {...props}>
            {!isResizing && children}
            {!!isResizing && (
              <Center height="100%">
                <Text align="center" bold size="h5" color="dark4">
                  {size.width}% x {size.height}px
                </Text>
              </Center>
            )}
          </Panel>

          {!disabled && (
            <>
              <Absolute top={0} left={0}>
                <Panel padding={0.3} bgColor="light2" radius={0} cursor="move" onMouseDown={move}>
                  <Icon name="BsArrowsMove" color="dark3" />
                </Panel>
              </Absolute>
              <Absolute right={-0.3} bottom={-0.3}>
                <Circle size={1} color="light4" cursor="se-resize" onMouseDown={resize} />
              </Absolute>
            </>
          )}
        </Relative>
      </Separator>
    </Absolute>
  )
}

export default CanvasCard
